import React, { useEffect, useState } from "react";
import { api, fmtError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/**
 * /settings/email — Super Admin only. Configure Resend API key + from-address.
 */
export default function EmailSettings() {
    const [cfg, setCfg] = useState({ resend_from: "", email_enabled: true, has_api_key: false, api_key_preview: "" });
    const [apiKey, setApiKey] = useState("");
    const [busy, setBusy] = useState(false);
    const [testTo, setTestTo] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/settings/email");
            setCfg(data);
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            const payload = { resend_from: cfg.resend_from, email_enabled: cfg.email_enabled };
            if (apiKey.trim()) payload.resend_api_key = apiKey.trim();
            await api.put("/settings/email", payload);
            toast.success("Email settings saved");
            setApiKey("");
            load();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const sendTest = async () => {
        if (!testTo.trim()) { toast.error("Enter a test recipient email"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/settings/email/test", { to: testTo.trim() });
            if (data.sent) toast.success(`Test email sent to ${testTo}`);
            else toast.error("Send failed — check API key & from-address");
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <div className="p-6 md:p-10 max-w-2xl" data-testid="email-settings-page">
            <div className="mb-8">
                <div className="overline">Super admin</div>
                <h1 className="font-display text-3xl">Email notifications</h1>
                <p className="text-sm text-slate-500 mt-2">
                    Configure Resend to send transactional emails (stage changes, EOD alerts, approvals, etc).
                    Get an API key from <a href="https://resend.com/api-keys" className="underline" target="_blank" rel="noreferrer">resend.com/api-keys</a>.
                </p>
            </div>

            <div className="border border-slate-200 bg-white p-6 space-y-5">
                <div>
                    <Label className="overline mb-1.5 block">Resend API key</Label>
                    <Input
                        type="password"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder={cfg.has_api_key ? `Current: ${cfg.api_key_preview} (leave blank to keep)` : "re_XXXXXXXXXXXXXXXX"}
                        data-testid="email-settings-api-key"
                        className="rounded-none border-slate-300 font-mono"
                    />
                </div>
                <div>
                    <Label className="overline mb-1.5 block">From address</Label>
                    <Input
                        value={cfg.resend_from || ""}
                        onChange={(e) => setCfg({ ...cfg, resend_from: e.target.value })}
                        placeholder="Pulse Notifications <notify@yourdomain.com>"
                        data-testid="email-settings-from"
                        className="rounded-none border-slate-300 font-mono"
                    />
                    <p className="text-[10px] font-mono text-slate-500 mt-1">
                        The sending domain must be verified in your Resend account.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        id="email_enabled"
                        type="checkbox"
                        checked={!!cfg.email_enabled}
                        onChange={(e) => setCfg({ ...cfg, email_enabled: e.target.checked })}
                        data-testid="email-settings-enabled"
                    />
                    <Label htmlFor="email_enabled" className="text-sm">Send email notifications</Label>
                </div>
                <div className="pt-2">
                    <Button
                        onClick={save}
                        disabled={busy}
                        data-testid="email-settings-save"
                        className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
                    >
                        {busy ? "Saving…" : "Save settings"}
                    </Button>
                </div>
            </div>

            <div className="mt-8 border border-slate-200 bg-white p-6 space-y-3">
                <h2 className="font-display text-lg">Send a test email</h2>
                <p className="text-xs text-slate-500">Confirm the setup works before triggering real notifications.</p>
                <div className="flex gap-2">
                    <Input
                        value={testTo}
                        onChange={(e) => setTestTo(e.target.value)}
                        placeholder="you@example.com"
                        data-testid="email-settings-test-to"
                        className="rounded-none border-slate-300 flex-1"
                    />
                    <Button
                        onClick={sendTest}
                        disabled={busy}
                        variant="outline"
                        data-testid="email-settings-test-send"
                        className="rounded-none border-slate-300"
                    >Send test</Button>
                </div>
            </div>

            <div className="mt-8 text-xs font-mono text-slate-500 space-y-1">
                <p>Notifications will be sent for:</p>
                <ul className="list-disc pl-5 space-y-0.5">
                    <li>Client Servicing pushes a campaign to Execution → Execution Head</li>
                    <li>Every stage change → Managers</li>
                    <li>Push to Delivery → Delivery Head</li>
                    <li>Delivery assignee added → the assigned user</li>
                    <li>Daily EOD submitted or updated → Reporting role</li>
                    <li>Detailed report uploaded → Execution Head</li>
                    <li>Detailed report approved/rejected → Reporting role</li>
                </ul>
            </div>
        </div>
    );
}
